import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined'
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined'
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined'
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined'
import LogoutIcon from '@mui/icons-material/Logout'
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined'
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined'
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined'
import {
  Avatar,
  Button,
  Divider,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { Box } from '@/components/ui'
import type { OrganizationDetail } from '@/definitions/organization'
import { useAuthStore } from '@/stores/authStore'
import { getInitials } from '@/utils/nameUtils'

import {
  NavItemButton,
  SidebarContainer,
  UserMenuHeader,
  UserProfileButton,
} from './OrgSidebar.styles'

interface OrgSidebarProps {
  org: OrganizationDetail
  orgId: string
}

const TABS = [
  { label: 'Overview', path: 'overview', icon: <DashboardOutlinedIcon fontSize='small' /> },
  { label: 'Locations', path: 'locations', icon: <PlaceOutlinedIcon fontSize='small' /> },
  { label: 'Staff', path: 'staff', icon: <PeopleOutlinedIcon fontSize='small' /> },
  { label: 'Product Flags', path: 'product-flags', icon: <TuneOutlinedIcon fontSize='small' /> },
  { label: 'Feature Flags', path: 'feature-flags', icon: <FlagOutlinedIcon fontSize='small' /> },
  { label: 'Intake Forms', path: 'intake-forms', icon: <AssignmentOutlinedIcon fontSize='small' /> },
]

export const OrgSidebar = ({ org, orgId }: OrgSidebarProps) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { user, signOut } = useAuthStore()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const isMenuOpen = Boolean(anchorEl)

  const userInitials = getInitials(user?.displayName, user?.email)

  const handleOpenMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget)
  }

  const handleCloseMenu = () => {
    setAnchorEl(null)
  }

  const handleSignOut = async () => {
    handleCloseMenu()
    await signOut()
  }

  return (
    <SidebarContainer>
      <Button
        variant='navBack'
        size='small'
        startIcon={<ArrowBackOutlinedIcon style={{ fontSize: 14 }} />}
        onClick={() => navigate('/orgs')}
      >
        Organizations
      </Button>

      <Box variant='flexColumn' px={1} pb={2}>
        <Typography variant='subtitle1' fontWeight={600} lineHeight={1.3}>
          {org.display_name}
        </Typography>
      </Box>

      <Divider variant='sectionDivider' />

      <Box variant='flexColumn' gap='xs'>
        {TABS.map((tab) => {
          const isActive = pathname.includes(`/orgs/${orgId}/${tab.path}`)
          return (
            <NavItemButton
              key={tab.path}
              active={isActive}
              startIcon={tab.icon}
              onClick={() => navigate(`/orgs/${orgId}/${tab.path}`)}
            >
              {tab.label}
            </NavItemButton>
          )
        })}
      </Box>

      <Box variant='spacer' />

      <Divider variant='sectionDivider' />

      <UserProfileButton onClick={handleOpenMenu} aria-label='User menu'>
        <Avatar variant='compactAvatar' src={user?.photoURL ?? undefined}>
          {userInitials}
        </Avatar>
        <Box variant='flexColumn' gap='none' fullWidth={false} minWidth={0} flex={1}>
          <Typography variant='caption' fontWeight={600} noWrap>
            {user?.displayName ?? 'User'}
          </Typography>
          <Typography variant='caption' color='text.secondary' noWrap>
            {user?.email}
          </Typography>
        </Box>
      </UserProfileButton>

      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { variant: 'selectMenu' } }}
      >
        <UserMenuHeader>
          <Typography variant='subtitle2'>{user?.displayName ?? 'User'}</Typography>
          <Typography variant='caption' color='text.secondary'>
            {user?.email}
          </Typography>
        </UserMenuHeader>
        <Divider />
        <MenuItem onClick={handleSignOut}>
          <ListItemIcon>
            <LogoutIcon fontSize='small' />
          </ListItemIcon>
          Sign Out
        </MenuItem>
      </Menu>
    </SidebarContainer>
  )
}
